import React from "react";

export interface MissingField {
  label: string;
  /** Optional anchor id of the form section so users can click to jump to it. */
  anchorId?: string;
}

interface MissingFieldsSummaryProps {
  missingFields: MissingField[];
  /** Optional override for the heading line. */
  heading?: string;
}

const HIGHLIGHT_CLASS = "missing-fields-target--flash";

const handleJumpToSection = (anchorId: string) => {
  const target = document.getElementById(anchorId);
  if (!target) return;

  const collapsedSections = target.matches("details.collapsible-form-section")
    ? [target]
    : Array.from(target.querySelectorAll("details.collapsible-form-section"));

  collapsedSections.forEach((section) => {
    if (section instanceof HTMLDetailsElement) {
      section.open = true;
    }
  });

  target.scrollIntoView({ behavior: "smooth", block: "center" });

  target.classList.remove(HIGHLIGHT_CLASS);
  // Force reflow so the animation can replay if the user clicks the same item twice.
  void target.offsetWidth;
  target.classList.add(HIGHLIGHT_CLASS);
  window.setTimeout(() => target.classList.remove(HIGHLIGHT_CLASS), 1800);
};

export const MissingFieldsSummary: React.FC<MissingFieldsSummaryProps> = ({
  missingFields,
  heading = "Complete these required sections to generate:",
}) => {
  if (missingFields.length === 0) return null;

  return (
    <div className="missing-fields-summary" role="status" aria-live="polite">
      <div className="missing-fields-summary__header">
        <svg
          className="missing-fields-summary__icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p className="missing-fields-summary__heading">{heading}</p>
      </div>
      <ul className="missing-fields-summary__list">
        {missingFields.map((field) => (
          <li key={field.label} className="missing-fields-summary__item">
            {field.anchorId ? (
              <button
                type="button"
                className="missing-fields-summary__link"
                onClick={() => handleJumpToSection(field.anchorId!)}
              >
                {field.label}
              </button>
            ) : (
              <span>{field.label}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
